import { faArrowUpRightFromSquare } from "@fortawesome/pro-duotone-svg-icons";
import { faTrash } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Button, InputNumber, Select, Skeleton, Space, Switch, Typography } from "antd";
import { isEqual } from "lodash";
import { useEffect, useReducer } from "react";
import { useEmailTemplatesQuery } from "../../../EmailTemplates/emailTemplateQueries";
import EmailTemplatePreview from "../../../EmailTemplates/EmailTemplatePreview";
import VSpace from "../../../common/VSpace";
import { timeOptions } from "../../../../common/timeOptions";
import { Tokens } from "../../../RichTextEditor/tokenModel";
import { defaultAutomationSchedule, findSelectedTemplate, getFollowUpTemplateOptions, toViewSchedule } from "./followUpScheduleModel";
import { reducer } from "./followUpScheduleReducer";
import { AutomationSchedule } from "../../../../domain/proposals/settings/proposalSettings";
import { useDefaultFollowUpScheduleQuery } from "../../../../domain/proposals/settings/proposalSettingsQueries";

type Props = {
  onScheduleChange?: (hasErrors: boolean, schedule: AutomationSchedule) => void
  onServerStateChange?: (schedule: AutomationSchedule) => void
  showDates?: boolean
  hideEditTemplatesLink?: boolean
}

const FollowUpSchedule = ({ onScheduleChange, onServerStateChange, showDates = false, hideEditTemplatesLink }: Props) => {
  const emailTemplatesQuery = useEmailTemplatesQuery()
  const emailTemplates = emailTemplatesQuery.data ?? []

  const defaultFollowUpAutomationScheduleQuery = useDefaultFollowUpScheduleQuery()
  const [state, dispatch] = useReducer(reducer, { schedule: defaultAutomationSchedule(), viewSchedule: toViewSchedule(defaultAutomationSchedule()) })
  const { schedule, viewSchedule } = state

  useEffect(() => {
    if(!defaultFollowUpAutomationScheduleQuery.isLoading && ( defaultAutomationSchedule().steps !== schedule.steps || defaultAutomationSchedule().enabled !== schedule.enabled)){
      if(schedule.steps !== defaultFollowUpAutomationScheduleQuery.data?.followUpAutomation.steps || schedule.enabled !== defaultFollowUpAutomationScheduleQuery.data?.followUpAutomation.enabled){
        onScheduleChange?.(viewSchedule.hasErrors, schedule)

        if(!defaultFollowUpAutomationScheduleQuery.data) return

        const automationSchedule = {steps: defaultFollowUpAutomationScheduleQuery.data.followUpAutomation.steps, enabled: defaultFollowUpAutomationScheduleQuery.data.followUpAutomationEnabled }
        if(isEqual(schedule, automationSchedule)){
          onServerStateChange?.(schedule)
        }
      }
    }
  }, [viewSchedule])

  useEffect(() => {
    if(defaultFollowUpAutomationScheduleQuery.data){
      const automationSchedule = {steps: defaultFollowUpAutomationScheduleQuery.data.followUpAutomation.steps, enabled: defaultFollowUpAutomationScheduleQuery.data.followUpAutomationEnabled }
      dispatch({ type: 'DEFAULT_SCHEDULE_CHANGED', value: automationSchedule })
      onServerStateChange?.(automationSchedule)
    }
  }, [defaultFollowUpAutomationScheduleQuery.data])

  if (defaultFollowUpAutomationScheduleQuery.isLoading || emailTemplatesQuery.isLoading) {
    return <VSpace>
      <Skeleton active />
    </VSpace>
  }

  return <VSpace>
    <Space>
        <Switch
          value={viewSchedule.isEnabled}
          onChange={(value)=> dispatch({ type: 'ENABLED_CHANGED', value}) } 
        />
        Send automatic follow-up emails.
    </Space>
    {schedule.enabled &&
      <VSpace>
        {viewSchedule.steps.map((step, index) => <Space key={step.id}>
          Send a reminder email after
          <InputNumber 
            value={step.afterDays} 
            min={1} 
            max={30}
            style={{width: '60px'}}
            onChange={value => {
              if (value != null) {
                dispatch({ type: 'AFTER_DAYS_CHANGED', id: step.id, value })
              }
            }}
            status={step.duplicateAfterDays ? 'error' : undefined}
          />
          <span>day{step.afterDays > 1 ? 's' : ''} { showDates && <span> on {step.displayDate}</span>} at</span>
          <Select popupMatchSelectWidth={false} options={timeOptions} value={step.atTime} onChange={value => dispatch({ type: 'AT_TIME_CHANGED', id: step.id, value })} />
          using template
          <Select 
            value={step.emailTemplateGuid}
            style={{width:'140px'}} 
            options={getFollowUpTemplateOptions(emailTemplates, index)} 
            onChange={value => dispatch({ type: 'EMAIL_TEMPLATE_CHANGED', id: step.id, value })}
            popupMatchSelectWidth={false}
          />
          <span>
            <EmailTemplatePreview template={findSelectedTemplate(step.emailTemplateGuid, index, emailTemplates)} tokens={Tokens} />
            <Button icon={<FontAwesomeIcon icon={faTrash} />}
              danger
              type="link"
              onClick={() => dispatch({ type: 'REMINDER_REMOVED', id: step.id })}
              disabled={!step.canDelete}
            />
          </span>
        </Space>)}
        <div style={{display: 'flex', justifyContent: 'space-between'}}>
          <Button
            onClick={() => dispatch({ type: 'REMINDER_ADDED' })}
            disabled={!viewSchedule.canAddReminder}
          >Add Another Reminder</Button>
          {!hideEditTemplatesLink &&
            <Typography.Link href="#!/setup#canned-responses" target="_blank" style={{marginRight: '  0px'}}>
              <FontAwesomeIcon icon={faArrowUpRightFromSquare} /> Edit Templates
          </Typography.Link>}
        </div>
      </VSpace>
    }
  </VSpace>
}

export default FollowUpSchedule
